// src/b2b/pages/TripSelection.jsx
import React, { useEffect, useMemo, useState, useRef } from 'react';
import { http } from '../../../api/http';
import { useLocation, useHistory } from 'react-router-dom';
import SelectRoom from 'b2b/components/SelectRoom';
import SelectFlight from 'b2b/components/SelectFlight';
import QuoteSummary from 'b2b/components/QuoteSummary';
import StepNav from 'b2b/components/StepNav';
import PageLoader from 'b2b/components/PageLoader';
import RoomCategories from 'b2b/components/RoomCategories';
import SelectMealPlan from 'b2b/components/SelectMealPlan';
import { distributePaxOverCombo } from 'b2b/utils/distributePaxOverCombo';
import { classify } from 'b2b/utils/classify';

const DEFAULT_PRICE = { stay: 0, fees: 0, taxes: 0, currency: 'EUR' };
const DEFAULT_PRODUCT = { hotelName: 'Séjour', city: '' };

const clean = (s) =>
  String(s || '')
    .toUpperCase()
    .replace(/^_?LT_/, '');
const iso = (d) => String(d || '').slice(0, 10);
const diffDays = (a, b) =>
  Math.max(1, Math.round((new Date(b || a) - new Date(a)) / 86400000));
const normalizeMoney = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

function calculateAgeAtDate(child, targetDate) {
  // Priorité à l'âge déjà calculé.
  const existingAge = Number(child?.ageOnEnd ?? child?.ageOnDeparture ?? child?.age);

  if (Number.isFinite(existingAge) && existingAge >= 0) {
    return Math.floor(existingAge);
  }

  const year = Number(child?.year);
  const month = Number(child?.month);
  const day = Number(child?.day);

  if (!year || !month || !day || !targetDate) {
    return null;
  }

  const birthDate = new Date(Date.UTC(year, month - 1, day));

  const referenceDate = new Date(targetDate);

  if (Number.isNaN(birthDate.getTime()) || Number.isNaN(referenceDate.getTime())) {
    return null;
  }

  let age = referenceDate.getUTCFullYear() - birthDate.getUTCFullYear();

  const monthDifference = referenceDate.getUTCMonth() - birthDate.getUTCMonth();

  const birthdayNotReached =
    monthDifference < 0 ||
    (monthDifference === 0 && referenceDate.getUTCDate() < birthDate.getUTCDate());

  if (birthdayNotReached) {
    age -= 1;
  }

  return Math.max(0, age);
}

function isInfantTraveller(child, endDate) {
  if (child?.isInfant === true) {
    return true;
  }

  const age = calculateAgeAtDate(child, endDate);

  return Number.isFinite(age) && age < 2;
}

function normalizeAlloc(alloc, infantsOverride) {
  const adults = Number(alloc?.adults ?? 0) || 0;
  const children = Number(alloc?.children ?? 0) || 0;

  // accepte infant OU infants
  let infants = Number(alloc?.infants ?? alloc?.infant ?? 0) || 0;

  // si tu veux forcer depuis search.infants (souvent global)
  if (infantsOverride !== undefined && infantsOverride !== null) {
    infants = Number(infantsOverride) || 0;
  }

  return { adults, children, infants };
}

function allocFromPgiValue(value) {
  const v = String(value || '');
  const mAdt = v.match(/adt:(\d+)/);
  const mChd = v.match(/chd:(\d+)/);
  const mInf = v.match(/inf:(\d+)/);
  return {
    adults: Number(mAdt?.[1] ?? 0) || 0,
    children: Number(mChd?.[1] ?? 0) || 0,
    infants: Number(mInf?.[1] ?? 0) || 0,
  };
}

const isQuoteEmpty = (p = {}) => {
  const noRooms =
    !(Array.isArray(p.rooms) && p.rooms.length) &&
    !(Array.isArray(p.roomsParsed) && p.roomsParsed.length);
  const noMeals =
    !(Array.isArray(p.mealPlans) && p.mealPlans.length) &&
    !(Array.isArray(p.mealPlansParsed) && p.mealPlansParsed.length);
  const noFlights = !(Array.isArray(p.flights) && p.flights.length);
  const noPrice = !(p.price && Number.isFinite(p.price.total) && p.price.total > 0);
  return noRooms && noMeals && noFlights && noPrice;
};

function getBookingsApiBase() {
  const token = localStorage.getItem('token_b2b');
  return token ? '/api/bookings' : '/api/public/bookings';
}

// Construit les catégories (#2) à partir du combo sélectionné (on respecte variant/baseType du back)
function buildPerRoomCategories(selectedComboItems = [], infants = 0) {
  const categories = [];

  selectedComboItems.forEach((item) => {
    //console.log('%csrc\b2bTripSelectionindex.jsx:53 item', 'color: #007acc;', item);
    const qty = Math.max(1, Number(item.qty || 1));
    const desc = String(item.description || item.desc || 'Chambre').trim();

    const baseType = item.baseType || classify(desc).baseType;
    const variant = item.variant || 'Standard';

    // ✅ code complet PGI
    // const pgiRoomCode = item.value || item.pgiRoomCode || null;
    const alloc = normalizeAlloc(item.allocation, /* override? */ null);
    const a = alloc.adults;
    const c = alloc.children;
    const i = alloc.infants;
    const pax = a + c + i;

    const baseRef =
      (item.value && String(item.value).split('|')[0]) ||
      item.pgiRoomCode?.split('|')?.[0] ||
      item.code ||
      null;

    const pgiRoomCode = baseRef
      ? `${baseRef}|adt:${a},chd:${c},inf:${i},pax:${pax}`
      : item.value || item.pgiRoomCode || null;

    const roomRef = pgiRoomCode?.includes('|') ? pgiRoomCode.split('|')[0] : null;

    for (let i = 0; i < qty; i++) {
      categories.push({
        title: `Chambre ${categories.length + 1}`,
        pattern: {
          code: item.code, // "R16729"
          baseType,
          variant,
          value: pgiRoomCode, // ✅ utile si tu veux
        },
        included: {
          id: `${item.code}-${i}`,
          desc,
          price: item.price?.unit || 0,
          isIncluded: true,

          // ✅ IMPORTANT : ce champ doit exister
          value: item.value || null,
          pgiRoomCode: item.value || null,
          alloc: item.allocation || null,
        },
        options: [
          {
            id: `${item.code}-${i}-opt0`,
            desc,
            price: item.price?.unit || 0,
            isIncluded: true,
            supplement: 0,
            saving: 0,

            // ✅ IMPORTANT : ce champ doit exister
            value: item.value || null,
            pgiRoomCode: item.value || null,
            alloc: item.allocation || null,
          },
        ],
        pattern: {
          code: roomRef || item.code,
          baseType,
          variant,
          value: item.value || null, // ✅ utile aussi
          allocation: item.allocation || null,
        },
        meta: { baseType, variant, infants: Number(infants || 0) > 0 },
      });
    }
  });

  return categories;
}

// function getRoomRef(room) {
//   // ex: "R16729|adt:1,chd:0,inf:0,pax:1" -> "R16729"
//   const raw = String(room?.pgiRoomCode || room?.value || room?.roomRef || '');
//   return raw.includes('|') ? raw.split('|')[0] : raw;
// }

// function buildPgiRoomCode(roomRef, adults, children, infants) {
//   const a = Number(adults || 0);
//   const c = Number(children || 0);
//   const i = Number(infants || 0);
//   const pax = a + c + i;
//   return `${roomRef}|adt:${a},chd:${c},inf:${i},pax:${pax}`;
// }

export default function TripSelectionPage() {
  const { state = {} } = useLocation();
  const history = useHistory();

  const search = state.search || {};
  const product = state.product || DEFAULT_PRODUCT;
  const priceSeed = state.priceSeed || DEFAULT_PRICE;
  const isValid = !!search?.origin && !!search?.startDate;

  // //console.log('%csrc\b2bTripSelectionindex.jsx:90 product', 'color: #007acc;', product);
  // //console.log('%csrc\b2bTripSelectionindex.jsx:91 search', 'color: #007acc;', search);

  const travellerAges = useMemo(() => {
    const travellers = Array.isArray(state?.formData?.childrenDOB)
      ? state.formData.childrenDOB
      : [];

    const endDate = search?.endDate || search?.startDate || null;

    const childAges = [];
    const infantAges = [];

    for (const traveller of travellers) {
      const age = calculateAgeAtDate(traveller, endDate);

      if (!Number.isFinite(age)) {
        continue;
      }

      if (isInfantTraveller(traveller, endDate)) {
        infantAges.push(age);
      } else {
        childAges.push(age);
      }
    }

    return {
      childAges,
      infantAges,
    };
  }, [state?.formData?.childrenDOB, search?.startDate, search?.endDate]);

  useEffect(() => {
    if (!isValid) history.replace('/');
  }, [isValid, history]);
  if (!isValid) return null;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, []);

  const skipFirstAutoFlightSelectRef = useRef(true);

  const [rooms, setRooms] = useState([]); // brut (debug)
  const [wsPrice, setWsPrice] = useState(null);
  const [flightPlans, setFlightPlans] = useState([]);
  const [mealPlans, setMealPlans] = useState([]);
  const [err, setErr] = useState(null);
  const [pending, setPending] = useState(0);

  const [roomsUI, setRoomsUI] = useState([]);
  const [defaultIndexUI, setDefaultIndexUI] = useState(null);

  const [prestations, setPrestations] = useState([]);
  const [selectedPrestations, setSelectedPrestations] = useState(
    Array.isArray(state.selectedPrestations) ? state.selectedPrestations : [],
  );

  // on part des valeurs venant du state (si on revient de /paiement)
  const initialRoom = state.selectedRoom || null;

  const initialMealPlan = state.selectedMealPlan || null;
  const initialFlight = state.selectedFlights || null;

  const infants = Number(search?.infants || 0);

  const initialCombo = Array.isArray(initialRoom?.combo)
    ? initialRoom.combo.map((item) => ({
        ...item,
        allocation: {
          ...item.allocation,
          infants, // <= ICI TU AJOUTES TON BÉBÉ
        },
      }))
    : [];

  const [selectedMealPlan, setSelectedMealPlan] = useState(initialMealPlan);
  const [selectedRoom, setSelectedRoom] = useState(initialRoom);
  const [selectedCombo, setSelectedCombo] = useState(initialCombo);
  const [roomCategories, setRoomCategories] = useState(
    initialCombo.length
      ? buildPerRoomCategories(initialCombo, Number(search?.infants || 0))
      : [],
  );
  const [variantsGroups, setVariantsGroups] = useState(new Map());
  const [selectedFlight, setSelectedFlight] = useState(
    initialFlight || {
      planId: null,
      transportTotal: 0,
      luggagesTotal: 0,
      currency: 'EUR',
      luggagesSelection: null,
      plan: null,
    },
  );
  // quote = payload retourné par /api/pgi/availability
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(false);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const productId =
    state?.product?._id || state?.productId || state?.formData?.productId || '';

  // 🔁 Quand on revient depuis la page Paiement, on restaure les choix
  useEffect(() => {
    // on attend que le devis PGI soit chargé, sinon on n’a pas encore les combos etc.
    if (!quote || loading) return;

    // 1) Chambre sélectionnée (combo, catégories…)
    if (state.selectedRoom && !selectedRoom) {
      const sr = state.selectedRoom;
      // //console.log('%csrc\b2bTripSelectionindex.jsx:163 sr', 'color: #007acc;', sr);
      setSelectedRoom(sr);

      const combo = Array.isArray(sr.combo) ? sr.combo : [];
      const infants = Number(search?.infants || 0);
      const comboWithInfants = combo.map((item) => ({
        ...item,
        allocation: {
          ...item.allocation,
          infants,
        },
      }));
      setSelectedCombo(comboWithInfants);

      if (comboWithInfants.length) {
        setRoomCategories(buildPerRoomCategories(comboWithInfants, infants));
      }
    }

    // 2) Type de pension
    if (state.selectedMealPlan && !selectedMealPlan) {
      setSelectedMealPlan(state.selectedMealPlan);
    }

    // 3) Vol + bagages
    if (state.selectedFlights && !selectedFlight?.planId) {
      setSelectedFlight(state.selectedFlights);
    }
  }, [
    quote,
    loading,
    state.selectedRoom,
    state.selectedMealPlan,
    state.selectedFlights,
    selectedRoom,
    selectedMealPlan,
    selectedFlight,
    search?.infants,
  ]);

  const roomsForUI = useMemo(() => roomsUI, [roomsUI]);

  useEffect(() => {
    if (!quote || loading) return;
    if (isQuoteEmpty(quote)) {
      alert(
        'Aucune disponibilité trouvée pour ce séjour. Vous allez être redirigé vers la page du produit.',
      );
      history.push(`/produit/${productId}`); // si v6 => navigate(`/produit/${productId}`);
    }
  }, [quote, loading, productId, history]); // v6: [quote, loading, productId, navigate]

  const nights = useMemo(() => {
    if (Number(state?.nights) > 0) return Number(state.nights);
    return Math.max(1, diffDays(search.startDate, search.endDate || search.startDate) - 1);
  }, [state?.nights, search]);

  const [b2bDiscount] = useState(() => {
    try {
      const raw = localStorage.getItem('userProfil');
      if (!raw) return 0;
      const user = JSON.parse(raw);
      const d = Number(user.discount || 0);
      return Number.isFinite(d) ? d : 0;
    } catch {
      return 0;
    }
  });

  function formatPrice(value, currency = 'EUR') {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency,
    }).format(Number(value || 0));
  }

  /* ------------------ Fetch disponibilité ------------------ */
  useEffect(() => {
    const productCode = search?.productCode || state.productCode;
    if (!productCode) return;

    const ctrl = new AbortController();
    let finished = false;
    const finish = () => {
      if (!finished) {
        setPending((p) => Math.max(0, p - 1));
        finished = true;
      }
    };

    setPending((p) => p + 1);
    (async () => {
      try {
        setErr(null);

        const noFlight =
          !!search?.noFlight ||
          String(search?.origin || '').toUpperCase() === 'XXX' ||
          String(search?.origin || '').toUpperCase() === 'TER';

        const body = {
          productCode,
          origin: noFlight ? 'XXX' : clean(search.fromCode || search.origin),
          beginDate: iso(search.startDate),
          nights,
          adults: Number(search.adults || 2),
          children: Number(search.children || 0),
          infants: Number(search.infants || 0),
          childAges: travellerAges.childAges,
          infantAges: travellerAges.infantAges,
          includeTaxes: false,
        };

        console.log('PGI TRAVELLERS SENT', {
          adults: body.adults,
          children: body.children,
          infants: body.infants,
          childAges: body.childAges,
          infantAges: body.infantAges,
        });

        const r = await fetch('/api/pgi/availability', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
          signal: ctrl.signal,
        });
        const data = await r.json();
        if (!r.ok) throw new Error(data?.error || 'PGI error');
        setQuote(data);
        console.log('%csrc\b2b\\TripSelectionindex.jsx:310 data', 'color: #007acc;', data);
        console.log('roomsParsed from API =', data?.roomsParsed?.[0]);
        console.log('combo[0] keys =', Object.keys(data?.roomsParsed?.[0]?.combo?.[0] || {}));
        console.log('combo[0] value =', data?.roomsParsed?.[0]?.combo?.[0]?.value);
        const cleanedRooms = (Array.isArray(data.rooms) ? data.rooms : []).filter(
          (x) => String(x.status).toLowerCase() === 'available',
        );

        setRooms(cleanedRooms);
        setWsPrice(data.price || null);
        setMealPlans(Array.isArray(data.mealPlansParsed) ? data.mealPlansParsed : []);
        // setFlightPlans(Array.isArray(data.flights) ? data.flights : []);

        //   const apiFlights  = Array.isArray(data.flights) ? data.flights : [];
        //  const globalBags  = Array.isArray(data.luggage) ? data.luggage : [];
        //  const flightsWithLuggage = apiFlights.map(p =>
        //    (p?.luggages && p.luggages.length) ? p : { ...p, luggages: globalBags }
        //  );
        //  //console.log('🧳 flightsWithLuggage =', flightsWithLuggage.map(f => ({id: f.id, lug: (f.luggages||[]).length})));
        //  setFlightPlans(flightsWithLuggage);

        const apiFlights = Array.isArray(data.flights) ? data.flights : [];
        setFlightPlans(apiFlights);

        setPrestations(
          Array.isArray(data.addOns)
            ? data.addOns
            : Array.isArray(data.prestations)
            ? data.prestations
            : [],
        );

        // ---------- Option A : roomsParsed (prioritaire) ----------
        const serverOptions = Array.isArray(data.roomsParsed) ? data.roomsParsed : null;
        if (serverOptions && serverOptions.length) {
          // 1) normalisation (pas d’inférence de variante)
          const norm = serverOptions.map((o, i) => ({
            code: String(o.code || o.Code || `ROOM_${i}`),
            desc: String(o.desc || o.description || o.name || `Chambre ${o.code || ''}`),
            total: Number(o.total ?? o.price ?? o?.price?.total ?? 0),
            included: !!o.included,
            currency: o.currency || 'EUR',
            combo: Array.isArray(o?.combo)
              ? o.combo.map((c, j) => ({
                  qty: Number(c.qty || 1),
                  // ✅ ROOM REF (Rxxxx)
                  code: String(c.code || `ROOMPART_${j}`),
                  // ✅ CODE PGI COMPLET : Rxxxx|adt:...,chd:...,inf:...,pax:...
                  value: c.value || c.pgiRoomCode || null,
                  description: String(c.desc || c.description || 'Chambre'),
                  // ✅ allocation pax par chambre (indispensable pour TripSelection)
                  allocation: c.allocation || c.alloc || null,
                  price: {
                    unit: Number(c.unitTotal ?? c.price?.unit ?? 0),
                    total: Number(
                      ((c.unitTotal ?? c.price?.unit ?? 0) * Number(c.qty || 1)).toFixed(2),
                    ),
                    currency: o.currency || 'EUR',
                  },

                  baseType: c.baseType || classify(c.desc || c.description || '').baseType,
                  variant: c.variant || 'Standard',
                  // À conserver
                  hasChildDiscount: c.hasChildDiscount === true,

                  childDiscount: c.childDiscount || null,
                }))
              : [
                  {
                    qty: 1,
                    code: String(o.code || `ROOM_${i}`),
                    value: o.value || o.pgiRoomCode || null,
                    description: String(o.desc || 'Chambre'),
                    allocation: o.allocation || { adults: 0, children: 0, infants: 0 },
                    price: {
                      unit: Number(o.total ?? 0),
                      total: Number(o.total ?? 0),
                      currency: o.currency || 'EUR',
                    },
                    baseType: 'Autre',
                    variant: 'Standard',
                  },
                ],
          }));

          function formatPrice(value, currency = 'EUR') {
            return new Intl.NumberFormat('fr-FR', {
              style: 'currency',
              currency,
            }).format(Number(value || 0));
          }

          /* ---------- XML: extraire UNIQUEMENT le 1er bloc Details comme default ---------- */
          function extractDefaultFromXmlDetails(xmlStr, fallbackCurrency = 'EUR') {
            if (!xmlStr || typeof xmlStr !== 'string') {
              return null;
            }

            let doc;

            try {
              doc = new DOMParser().parseFromString(xmlStr, 'text/xml');
            } catch {
              return null;
            }

            const parserError = doc.querySelector('parsererror');

            if (parserError) {
              console.error('XML rooms parse error:', parserError.textContent);

              return null;
            }

            const getText = (node, selector) =>
              node.querySelector(selector)?.textContent?.trim() || '';

            const parsePrice = (roomEl) => {
              const priceEl = roomEl.querySelector(':scope > Occupancies > Price');

              if (!priceEl) {
                return {
                  total: 0,
                  currency: fallbackCurrency,
                };
              }

              const rawValue = priceEl.getAttribute('Value') || priceEl.getAttribute('value');

              const decimals = Number(
                priceEl.getAttribute('Decimals') || priceEl.getAttribute('decimals') || 0,
              );

              const currency =
                priceEl.getAttribute('Currency') ||
                priceEl.getAttribute('currency') ||
                fallbackCurrency;

              let amount = Number(rawValue);

              if (!Number.isFinite(amount)) {
                amount = 0;
              }

              if (decimals > 0) {
                amount /= Math.pow(10, decimals);
              }

              return {
                total: Number(amount.toFixed(2)),
                currency,
              };
            };

            const detailsBlocks = Array.from(doc.querySelectorAll('Rooms[What="Details"]'));

            if (!detailsBlocks.length) {
              return null;
            }

            const roomRefFromValue = (value = '') => {
              const normalized = String(value || '').trim();

              if (!normalized) return null;

              return normalized.split('|')[0] || null;
            };

            const detailRooms = detailsBlocks.flatMap((block) =>
              Array.from(block.children).filter((node) => node.tagName === 'Room'),
            );

            const items = detailRooms.map((roomEl) => {
              const codeAttr = roomEl.getAttribute('Code') || 'Room';

              const value = roomEl.querySelector(':scope > Code')?.getAttribute('Value') || '';

              const roomRef = roomRefFromValue(value);

              const qty = Math.max(1, Number(roomEl.getAttribute('Quantity') || 1));

              const description =
                getText(roomEl, ':scope > Description') || `Chambre ${codeAttr}`;

              const { total: unit, currency } = parsePrice(roomEl);

              return {
                code: roomRef || codeAttr,
                roomRef,
                description,
                qty,

                price: {
                  unit,
                  total: Number((unit * qty).toFixed(2)),
                  currency,
                },

                value,
              };
            });

            const total = Number(
              items
                .reduce((sum, item) => sum + (Number(item?.price?.total) || 0), 0)
                .toFixed(2),
            );

            const currency = items[0]?.price?.currency || fallbackCurrency;

            console.log('XML DEFAULT ROOMS FIX', {
              items: items.map((item) => ({
                description: item.description,
                qty: item.qty,
                unit: item.price.unit,
                total: item.price.total,
              })),
              total,
              currency,
            });

            return {
              items,
              total,
              currency,
            };
          }

          // 2) 🔒 forcer l’option incluse d’après Rooms What="Details" (si dispo)
          const xmlDefault = extractDefaultFromXmlDetails(data.roomsXml, 'EUR');
          console.log(
            '%csrc\b2bTripSelectionindex.jsx:487 xmlDefault',
            'color: #007acc;',
            xmlDefault,
          );
          const normStr = (s) =>
            String(s || '')
              .replace(/\s+/g, ' ')
              .trim()
              .toLowerCase();
          const sig = (items) =>
            (items || [])
              .map((it) => `${normStr(it.description || it.desc)}|${Number(it.qty || 1)}`)
              .sort()
              .join('||');
          console.log(
            '%csrc\b2bTripSelectionindex.jsx:504 sig',
            'color: #007acc;',
            sig(xmlDefault.items),
          );

          const xmlSig = xmlDefault ? sig(xmlDefault.items) : null;
          console.log('%csrc\b2bTripSelectionindex.jsx:504 xmlSig', 'color: #007acc;', xmlSig);
          let withIncluded = norm;
          if (xmlSig) {
            const idx = norm.findIndex(
              (o) =>
                sig(
                  (o.combo || []).map((c) => ({
                    description: c.description || c.desc,
                    qty: c.qty,
                  })),
                ) === xmlSig,
            );
            if (idx >= 0) {
              withIncluded = norm.map((x, i) => ({ ...x, included: i === idx }));
            } else {
              const firstIncludedIdx = Math.max(
                0,
                norm.findIndex((x) => x.included),
              );
              withIncluded = norm.map((x, i) => ({ ...x, included: i === firstIncludedIdx }));
            }
          } else {
            const firstIncludedIdx = Math.max(
              0,
              norm.findIndex((x) => x.included),
            );
            withIncluded = norm.map((x, i) => ({ ...x, included: i === firstIncludedIdx }));
          }
          console.log(
            '%csrc\b2bTripSelectionindex.jsx:532 withIncluded',
            'color: #007acc;',
            withIncluded,
          );
          // 3) trier : incluse en tête, puis prix
          const ordered = withIncluded.sort(
            (a, b) => b.included - a.included || a.total - b.total,
          );

          if (xmlDefault?.items?.length) {
            const incIdx = ordered.findIndex((o) => o.included);
            if (incIdx >= 0) {
              ordered[incIdx] = {
                ...ordered[incIdx],

                // optionnel : forcer total/currency sur la valeur XML
                total: xmlDefault.total ?? ordered[incIdx].total,
                currency: xmlDefault.currency ?? ordered[incIdx].currency,

                // ✅ combo venant du XML (Rxxxx + value complète)
                combo: xmlDefault.items.map((it) => ({
                  qty: Number(it.qty || 1),
                  code: String(it.code || it.roomRef || 'Room'),
                  roomRef: it.roomRef || null,
                  value: it.value || null,
                  description: String(it.description || 'Chambre'),
                  allocation: allocFromPgiValue(it.value),
                  price: it.price || {
                    unit: 0,
                    total: 0,
                    currency: xmlDefault.currency || 'EUR',
                  },
                  baseType: classify(it.description || '').baseType,
                  variant: 'Standard',
                })),
              };
            }
          }

          // 4) mapping UI (on garde le combo tel quel)
          const ui = ordered.map((opt, idx) => {
            const calculatedRoomsTotal = comboTotal(opt.combo);
            const roomsTotal =
              calculatedRoomsTotal > 0 ? calculatedRoomsTotal : Number(opt.total || 0);

            return {
              code: opt.included ? 'DEFAULT_COMBO' : `ALT_${idx}`,
              status: 'Available',
              quantity: 1,
              description: opt.desc,

              // Toujours utiliser le total issu du combo lorsque disponible
              roomsTotal,
              total: roomsTotal,

              price: {
                total: roomsTotal,
                currency: opt.currency || 'EUR',
              },

              ui: {
                hidePrice: opt.included,
                isDefault: opt.included,
                included: opt.included,
              },

              combo: opt.combo,
            };
          });

          setRoomsUI(ui);
          console.log('%csrc\b2bTripSelectionindex.jsx:556 ui', 'color: #007acc;', ui);
          // //console.log(
          //   'DEBUG combo',
          //   ui?.[0]?.combo?.map((x) => ({
          //     code: x.code,
          //     desc: x.description,
          //     value: x.value,
          //     allocation: x.allocation,
          //   })),
          // );
          setDefaultIndexUI(0);

          // 5) catégories (#2) → partir du combo inclus + passer variantsGroups correctement
          const selectedComboItems = ui[0]?.combo || [];
          setRoomCategories(
            buildPerRoomCategories(selectedComboItems, Number(search.infants || 0)),
          );

          return; // ✅ on s’arrête ici (pas de fallback)
        }

        // ---------- Pas de roomsParsed : ne fabrique rien en local ----------
        setRoomsUI([]);
        setDefaultIndexUI(null);
        setRoomCategories([]);
      } catch (e) {
        if (e.name !== 'AbortError') setErr(e.message || 'Erreur disponibilité PGI');
      } finally {
        finish();
      }
    })();

    return () => {
      ctrl.abort();
      finish();
    };
  }, [
    state.product?.code,
    state.productCode,
    search?.productCode,
    search?.origin,
    search?.fromCode,
    search?.startDate,
    nights,
    search?.adults,
    search?.children,
    search?.infants,
    variantsGroups, // ⚠️ pour que les catégories se recalculent si le groupe change
  ]);

  async function repriceWithFlight(planCode) {
    if (!planCode) return;

    const roomCode = selectedRoom?.combo?.[0]?.value || null; // ex: "R19032|adt:2,chd:0,inf:0,pax:2"
    const selectedRooms = (selectedRoom?.combo || []).map((r, idx) => ({
      value: r.value, // ✅ "R16729|adt:1,chd:0,inf:0,pax:1"
      travellerRefs: [`T${idx + 1}`], // T1, T2
    }));

    const mealPlanCode = selectedMealPlan?.code || null; // si tu as un state de pension, sinon null

    setQuoteLoading(true);
    try {
      const body = {
        productCode: search?.productCode || state.productCode,
        origin: clean(search.fromCode || search.origin),
        beginDate: iso(search.startDate),
        nights,
        adults: Number(search.adults || 2),
        children: Number(search.children || 0),
        infants: Number(search.infants || 0),
        childAges: travellerAges.childAges,
        infantAges: travellerAges.infantAges,
        includeTaxes: true,
        // 🔥 clé du recalcul
        flightCode: planCode, // gds_...
        roomCode,
        roomsCodes: selectedRooms,
        mealPlanCode,
      };
      console.log('%csrc\b2bTripSelectionindex.jsx:668 body', 'color: #007acc;', body);
      const r = await fetch('/api/pgi/availability', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      const data = await r.json();
      if (!r.ok) throw new Error(data?.error || 'PGI reprice failed');

      // ✅ on remplace UNIQUEMENT le price PGI
      const next = data?.price;
      const hasSomething =
        next &&
        (Number(next.total) > 0 || (Array.isArray(next.items) && next.items.length > 0));

      if (hasSomething) {
        setWsPrice(next);
      } else {
        // on garde l'ancien prix au lieu de tout mettre à zéro
        console.warn('PGI returned empty price, keeping previous wsPrice', next);
      }
    } catch (e) {
      console.error('REPRICE_FAILED', e);
    } finally {
      setQuoteLoading(false);
    }
  }

  /* ------------------ Prix bloc récap ------------------ */
  const priceToShow = useMemo(() => {
    if (!wsPrice) return priceSeed;

    const currency = wsPrice.currency || 'EUR';

    // ✅ Ne plus diviser les montants ici.
    // Le backend a déjà transformé les montants PGI en euros.
    const items = (wsPrice.items || []).map((it) => ({
      ...it,
      unit: normalizeMoney(it.unit),
      total: normalizeMoney(it.total),
    }));

    const itemsSum = Number(
      items.reduce((s, it) => s + (Number(it.total) || 0), 0).toFixed(2),
    );

    const rawTotal = normalizeMoney(wsPrice.total);

    const total = rawTotal > 0 ? rawTotal : itemsSum;

    return {
      stay: total, // utilisé ailleurs comme total PGI
      total,
      fees: 0,
      taxes: 0,
      currency,
      items,
    };
  }, [wsPrice, priceSeed]);

  const includedServices = useMemo(() => {
    const items = Array.isArray(priceToShow?.items) ? priceToShow.items : [];

    const norm = (s) =>
      String(s || '')
        .replace(/\s+/g, ' ')
        .trim();

    return (
      items
        .map((it) => {
          const label = norm(it.label || it.desc || it.description || it.name);
          const role = norm(it.role || it.Role || it.kind || it.type); // selon ce que renvoie ton back
          const total = Number(it.total ?? it.unit ?? 0);

          return { label, role, total };
        })
        .filter((x) => {
          // ✅ On cible uniquement les Supplements
          const isSupplement = String(x.role || '').toLowerCase() === 'supplement';
          // ✅ "Inclus" : total = 0 (comme ton XML "En formule hors boissons" = 0)
          // const isIncluded = Number.isFinite(x.total) && x.total === 0;

          return isSupplement && x.label;
        })
        // éviter doublons si ça arrive
        .filter((x, i, arr) => arr.findIndex((y) => y.label === x.label) === i)
    );
  }, [priceToShow?.items]);

  const defaultIndex = useMemo(() => defaultIndexUI, [defaultIndexUI]);

  const num = (v) => (v == null ? 0 : Number(v) || 0);
  const roomExtra = useMemo(() => roomDelta, [roomDelta]);
  const flightsTotal = useMemo(() => {
    if (wsPrice) return num(selectedFlight?.luggagesTotal); // ou 0 si bagages déjà inclus
    return num(selectedFlight?.transportDelta) + num(selectedFlight?.luggagesTotal);
  }, [wsPrice, selectedFlight]);

  const prestationsTotal = useMemo(
    () => selectedPrestations.reduce((sum, p) => sum + (Number(p.total) || 0), 0),
    [selectedPrestations],
  );

  const mealPlanExtra = useMemo(() => {
    if (!selectedMealPlan || selectedMealPlan.included) return 0;
    return num(selectedMealPlan.total);
  }, [selectedMealPlan]);

  const grandTotal = useMemo(() => {
    const baseTotal = num(priceToShow.total ?? priceToShow.stay);

    if (wsPrice) {
      return Number(
        (baseTotal + roomExtra + flightsTotal + prestationsTotal + mealPlanExtra).toFixed(2),
      );
    }

    return Number(
      (
        baseTotal +
        num(priceToShow.fees) +
        num(priceToShow.taxes) +
        roomExtra +
        flightsTotal +
        prestationsTotal +
        mealPlanExtra
      ).toFixed(2),
    );
  }, [wsPrice, priceToShow, roomExtra, flightsTotal, prestationsTotal, mealPlanExtra]);

  // Recalcule les catégories quand la répartition change
  useEffect(() => {
    const current = roomsForUI?.find((_, i) => i === defaultIndex) ?? roomsForUI?.[0];
    const selectedComboItems = selectedRoom?.combo || current?.combo || [];
    if (selectedComboItems?.length) {
      setRoomCategories(
        buildPerRoomCategories(selectedComboItems, Number(search.infants || 0)),
      );
    }
  }, [selectedRoom, roomsForUI, defaultIndex, search.infants]);

  const noFlight =
    !!search?.noFlight ||
    String(search?.origin || '').toUpperCase() === 'XXX' ||
    String(search?.origin || '').toUpperCase() === 'TER'; // si jamais

  // ⬇️ ajoute deux paramètres: roomCategories + nights
  async function saveRoom(bookingId, room, mealPlan, categories = [], nights = 0) {
    const token = localStorage.getItem('token_b2b');
    const guestToken = localStorage.getItem('booking_guest_token');

    const headers = {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(!token && guestToken ? { 'X-Booking-Token': guestToken } : {}),
    };

    // 1) items par chambre depuis roomCategories
    let items = [];
    if (Array.isArray(categories) && categories.length) {
      items = categories.map((cat, idx) => {
        console.log('%csrc\b2bTripSelectionindex.jsx:627 cat', 'color: #007acc;', cat);
        const opt = (cat.options || []).find((o) => o.isIncluded) || cat.included || {};
        console.log('%csrc\b2bTripSelectionindex.jsx:678 opt', 'color: #007acc;', opt);
        return {
          label: cat.title || `Chambre ${idx + 1}`,
          roomType: opt.desc || '',
          pgiRoomCode:
            opt.value ||
            opt.pgiRoomCode ||
            (cat?.pattern?.code && opt.alloc
              ? `${cat.pattern.code}|adt:${opt.alloc.adults || 0},chd:${
                  opt.alloc.children || 0
                },inf:${opt.alloc.infants || opt.alloc.infant || 0},pax:${
                  (opt.alloc.adults || 0) +
                  (opt.alloc.children || 0) +
                  (opt.alloc.infants || opt.alloc.infant || 0)
                }`
              : null),
          alloc: opt.alloc || cat?.meta?.alloc || cat?.pattern?.allocation || null,
          unitPrice: Number(opt.price || 0),
          quantity: 1,
          nights: Number(nights || 0),
          total: Number(opt.price || 0),
        };
      });
    }

    const roomsTotal = items.reduce((s, it) => s + (Number(it.total) || 0), 0);
    console.log('%csrc\b2bTripSelectionindex.jsx:707 items', 'color: #007acc;', items);
    const body = {
      room: {
        code: room.code || room.id || null,
        description: room.description || room.label || '',
        price: {
          total: room.price?.total || 0,
          currency: room.currency || 'EUR',
        },
        items,
        roomsTotal,

        fits: room.fits ?? true,

        // pension
        boardCode: mealPlan?.code || null,
        boardLabel: mealPlan?.desc || mealPlan?.label || null,
        boardSupplement: Number(mealPlan?.total || 0),
      },
    };

    const base = getBookingsApiBase();
    const res = await fetch(`${base}/${bookingId}/room`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    });

    const data = await res.json();
    if (!res.ok || !data?.ok)
      throw new Error(data?.error || data?.message || 'ROOM_SAVE_FAILED');
  }

  console.log('selectedRoom.combo', selectedRoom?.combo);
  console.log('roomCategories', roomCategories);

  function computeBaggageFromPlan(plan) {
    const included = !!plan?.baggageIncluded;
    const option = !!plan?.canBuyExtraBaggage;
    const status = included ? 'included' : option ? 'option' : 'none';
    return { status, included, option };
  }

  async function saveFlights(bookingId, flight) {
    const baggage = flight?.baggage || computeBaggageFromPlan(flight?.plan);
    console.log('saveFlight received baggage:', flight); // ✅ debug
    console.log('saveFlight received baggage:', baggage); // ✅ debug

    const token = localStorage.getItem('token_b2b');
    const guestToken = localStorage.getItem('booking_guest_token');

    const headers = {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(!token && guestToken ? { 'X-Booking-Token': guestToken } : {}),
    };

    // On essaye plusieurs chemins possibles pour trouver les vrais segments
    const rawOutbound =
      (Array.isArray(flight.outbound) && flight.outbound) ||
      (flight.plan && Array.isArray(flight.plan.outbound) && flight.plan.outbound) ||
      [];
    const rawInbound =
      (Array.isArray(flight.inbound) && flight.inbound) ||
      (flight.plan && Array.isArray(flight.plan.inbound) && flight.plan.inbound) ||
      [];

    const normalizeSegment = (s, direction) => ({
      direction,
      from: s.from || s.origin || s.fromCode || s.dep || s.departureAirport || null,
      to: s.to || s.destination || s.toCode || s.arr || s.arrivalAirport || null,
      carrier: s.carrier || s.carrierCode || s.marketingCarrier || '',
      flightNumber: s.flightNumber || s.code || s.number || '',
      departure: s.begin || s.departure || s.departureDateTime || null,
      arrival: s.end || s.arrival || s.arrivalDateTime || null,
    });

    const segments = [
      ...rawOutbound.map((s) => normalizeSegment(s, 'outbound')),
      ...rawInbound.map((s) => normalizeSegment(s, 'inbound')),
    ];

    console.log('TripSelection saveFlights flight.baggage =', flight?.baggage);

    const payload = {
      planId: flight.planId,
      productCode: flight.productCode || flight.plan?.code || null,
      transportTotal: Number(flight.transportTotal || 0),
      luggagesTotal: Number(flight.luggagesTotal || 0),
      luggagesSelection: flight.luggagesSelection || null,
      baggage,
      transportBreakdown: flight.transportBreakdown || {
        description: flight.transportLabel,
        unit: flight.transportUnit,
        qty: flight.transportQty,
        currency: flight.currency || 'EUR',
      },
      grandTotal: flight.grandTotal || null,
      segments, // 👈 maintenant ce sera non-vide si on a trouvé des tronçons
    };

    const base = getBookingsApiBase();
    const res = await fetch(`${base}/${bookingId}/flights`, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
    });

    const data = await res.json();
    if (!res.ok || !data?.ok) {
      throw new Error(data?.error || data?.message || 'FLIGHTS_SAVE_FAILED');
    }
  }

  function AddOnsSelection({ groups = [], selected = [], paxCount = 1, onChange }) {
    const selectedByGroup = useMemo(() => {
      const map = new Map();
      selected.forEach((x) => map.set(x.groupId, x));
      return map;
    }, [selected]);

    const updateSelection = (nextItem) => {
      const next = selected.filter((x) => x.groupId !== nextItem.groupId);
      onChange([...next, nextItem]);
    };

    const removeGroup = (groupId) => {
      onChange(selected.filter((x) => x.groupId !== groupId));
    };

    const buildSelectedItem = (group, option, qtyOverride) => {
      const max = Number(option?.quantity?.max || paxCount || 1);
      const min = Math.max(1, Number(option?.quantity?.min || 1));
      const qty = Math.min(Math.max(Number(qtyOverride || paxCount || 1), min), max);
      const unitPrice = Number(option?.price?.unit || 0);

      return {
        groupId: group.id,
        groupLabel: group.label,
        optionId: option.id,
        code: option.code,
        label: option.label,
        unitPrice,
        qty,
        total: Number((unitPrice * qty).toFixed(2)),
        currency: option?.price?.currency || group.currency || 'EUR',
        priceRef: option?.price?.ref || null,
        quantityRule: option?.quantity || null,
      };
    };

    return (
      <div>
        {groups.map((group) => {
          const checked = selectedByGroup.has(group.id);
          const selectedItem = selectedByGroup.get(group.id);
          const options = Array.isArray(group.options) ? group.options : [];
          const cheapest = [...options].sort(
            (a, b) => Number(a?.price?.unit || 0) - Number(b?.price?.unit || 0),
          )[0];

          return (
            <div key={group.id} className="border rounded p-3 mb-2">
              <div className="d-flex align-items-center justify-content-between">
                <label className="d-flex align-items-center gap-2 mb-0">
                  <input
                    type="checkbox"
                    checked={checked}
                    className="mr-2"
                    onChange={(e) => {
                      if (!e.target.checked) {
                        removeGroup(group.id);
                        return;
                      }

                      if (cheapest) {
                        updateSelection(buildSelectedItem(group, cheapest));
                      }
                    }}
                  />

                  <span>
                    {group.label}
                    {Number(group.minPrice || 0) > 0 && (
                      <span className="text-muted ms-1">
                        à partir de {formatPrice(group.minPrice, group.currency)}
                      </span>
                    )}
                  </span>
                </label>
              </div>

              {checked && options.length > 1 && (
                <div className="mt-3 ps-4">
                  <select
                    className="form-control form-select-sm mb-2"
                    value={selectedItem?.optionId || ''}
                    onChange={(e) => {
                      const opt = options.find((o) => o.id === e.target.value);
                      if (opt)
                        updateSelection(buildSelectedItem(group, opt, selectedItem?.qty));
                    }}
                  >
                    {options.map((opt) => (
                      <option key={opt.id} value={opt.id}>
                        {opt.label} - {formatPrice(opt?.price?.unit, opt?.price?.currency)}
                      </option>
                    ))}
                  </select>

                  <div className="d-flex align-items-center gap-2">
                    <span className="small text-muted">Quantité</span>
                    <input
                      type="number"
                      className="form-control form-control-sm"
                      style={{ width: 90 }}
                      min={1}
                      max={selectedItem?.quantityRule?.max || paxCount}
                      value={selectedItem?.qty || 1}
                      onChange={(e) => {
                        const opt = options.find((o) => o.id === selectedItem?.optionId);
                        if (opt)
                          updateSelection(buildSelectedItem(group, opt, e.target.value));
                      }}
                    />
                    <strong className="ms-auto">
                      {formatPrice(selectedItem?.total, selectedItem?.currency)}
                    </strong>
                  </div>
                </div>
              )}

              {checked && options.length === 1 && selectedItem && (
                <div className="mt-2 small text-muted ps-4">
                  {selectedItem.label} — {selectedItem.qty} ×{' '}
                  {formatPrice(selectedItem.unitPrice, selectedItem.currency)}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  async function savePrestations(bookingId, selectedPrestations = []) {
    if (!bookingId) return;
    if (!Array.isArray(selectedPrestations) || selectedPrestations.length === 0) return;

    const token = localStorage.getItem('token_b2b');
    const guestToken = localStorage.getItem('booking_guest_token');

    const headers = {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(!token && guestToken ? { 'X-Booking-Token': guestToken } : {}),
    };

    const prestations = selectedPrestations.map((p) => ({
      groupId: p.groupId || null,
      groupLabel: p.groupLabel || null,

      optionId: p.optionId || null,
      code: p.code || null,
      label: p.label || 'Prestation',

      quantity: Number(p.qty || p.quantity || 1),
      unitPrice: Number(p.unitPrice || p.unit || 0),
      total: Number(p.total || 0),
      currency: p.currency || priceToShow?.currency || 'EUR',

      priceRef: p.priceRef || null,
      quantityRule: p.quantityRule || null,
    }));

    const base = getBookingsApiBase();

    const res = await fetch(`${base}/${bookingId}/prestations`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ prestations }),
    });

    const data = await res.json();

    if (!res.ok || !data?.ok) {
      throw new Error(data?.error || data?.message || 'PRESTATIONS_SAVE_FAILED');
    }
  }

  async function saveQuote(bookingId, roomCategories = [], selectedPrestations = []) {
    if (!bookingId) return;

    const token = localStorage.getItem('token_b2b');
    const guestToken = localStorage.getItem('booking_guest_token');

    const headers = {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(!token && guestToken ? { 'X-Booking-Token': guestToken } : {}),
    };

    const lines = [];

    const baseItems = Array.isArray(priceToShow?.items) ? priceToShow.items : [];
    //console.log('%csrc\b2bTripSelectionindex.jsx:713 baseItems', 'color: #007acc;', baseItems);

    // nb de passagers payants (adultes + enfants, sans bébés)
    const paxCount = Number(search?.adults || 0) + Number(search?.children || 0) || 1;

    /* ----------------- 0) Chambres (depuis selectedRoom, pas le PGI) ----------------- */
    if (Array.isArray(roomCategories) && roomCategories.length) {
      roomCategories.forEach((cat, idx) => {
        const opt = (cat.options || []).find((o) => o.isIncluded) || cat.included || {};
        const unit = Number(opt.price || 0);

        if (!unit) return;

        lines.push({
          kind: 'room',
          code: cat.pattern?.code || selectedRoom?.code || null,
          label: cat.title || `Chambre ${idx + 1}`, // "Chambre 1", "Chambre 2"
          unit,
          qty: 1,
          total: unit,
          currency: priceToShow.currency || 'EUR',
        });
      });
    }

    const isMealPlanLine = (label, code) => {
      const low = String(label || '').toLowerCase();
      if (low.includes('pension')) return true; // “pension complète”, etc.
      if (low.includes('supplément pension')) return true;
      if (String(code || '').toUpperCase() === 'FMPC') return true; // ton cas
      return false;
    };

    /* ----------------- 1) Lignes PGI : taxes / transfert / vol de base ----------------- */
    baseItems.forEach((it, idx) => {
      const label = it.label || it.desc || it.description || it.name || `Ligne ${idx + 1}`;
      const code = it.code || it.id || null;

      if (selectedMealPlan && isMealPlanLine(label, code)) return;

      const low = label.toLowerCase();
      let kind = 'other';
      if (low.includes('chambre')) kind = 'room';
      else if (low.includes('taxes')) kind = 'tax';
      else if (low.includes('transfert')) kind = 'transfer';
      else if (low.includes('vol')) kind = 'flight_base';

      // ❌ on ignore complètement les chambres venant du PGI
      if (kind === 'room') return;

      // total renvoyé par PGI pour la ligne
      let total = Number(it.total ?? it.unit ?? 0);
      if (!Number.isFinite(total)) total = 0;

      // valeurs par défaut
      let qty = 1;
      let unit = Number(it.unit ?? it.total ?? 0);
      if (!Number.isFinite(unit) || !unit) unit = total;

      // 👉 pour taxes / transfert / vol de base :
      //  - total = montant pour toute la résa (ex : 225 €)
      //  - on veut : unit = prix par pax, qty = nb de pax (ex : 75 x 3)
      if (['tax', 'transfer', 'flight_base'].includes(kind) && paxCount > 0) {
        qty = paxCount;
        unit = Number((total / qty).toFixed(2));
        total = Number((unit * qty).toFixed(2)); // sécurise l’arrondi
      }

      if (!total && !unit) return; // on saute les lignes vides

      lines.push({
        kind,
        code,
        label,
        unit,
        qty,
        total,
        currency: priceToShow.currency || 'EUR',
      });
    });

    /* ----------------- 2) Supplément pension complète ----------------- */
    if (selectedMealPlan && selectedMealPlan.total) {
      const boardTotal = Number(selectedMealPlan.total || 0);

      lines.push({
        kind: 'board',
        code: selectedMealPlan.code || null,
        label: selectedMealPlan.desc || 'Supplément pension complète',
        unit: boardTotal,
        qty: 1,
        total: boardTotal,
        currency: selectedMealPlan.currency || 'EUR',
      });
    }

    /* ----------------- 3) Supplément vol (delta) + bagages ----------------- */
    if (selectedFlight) {
      // delta vol : 46,80 € (si tu en as un)
      if (selectedFlight && !wsPrice) {
        const delta = Number(
          selectedFlight.transportDelta ?? selectedFlight.transportTotalDelta ?? 0,
        );
        //console.log('%csrc\b2bTripSelectionindex.jsx:807 delta', 'color: #007acc;', delta);
        if (delta) {
          lines.push({
            kind: 'flight_delta',
            code: selectedFlight.productCode, // gds_AMh...
            label: selectedFlight.deltaLabel || 'Supplément vol',
            unit: delta,
            qty: 1,
            total: delta,
            currency: selectedFlight.currency || 'EUR',
          });
        }
      }

      // 🧳 bagages : déjà ok, on garde le principe mais on impose qty = nb de pax
      const sel = selectedFlight.luggagesSelection || {};
      const totalBags = Number(selectedFlight.luggagesTotal || sel.total || 0);

      if (totalBags) {
        const qty = paxCount; // ex : 2 adultes → qty = 2
        const unit = Number((totalBags / qty).toFixed(2));
        const total = Number((unit * qty).toFixed(2)); // sécurité arrondi

        lines.push({
          kind: 'baggage',
          code: null,
          label: 'Bagages',
          unit,
          qty,
          total,
          currency: selectedFlight.currency || 'EUR',
        });
      }
    }

    selectedPrestations.forEach((p) => {
      const total = Number(p.total || 0);
      if (!total) return;

      lines.push({
        kind: 'addon',
        code: p.code,
        label: p.groupLabel ? `${p.groupLabel} - ${p.label}` : p.label,
        unit: Number(p.unitPrice || 0),
        qty: Number(p.qty || 1),
        total,
        currency: p.currency || priceToShow.currency || 'EUR',
      });
    });

    // 4) Si aucune ligne, on ne fait rien
    if (!lines.length) return;

    // 5) Envoi du devis au backend
    const base = getBookingsApiBase();
    const res = await fetch(`${base}/${bookingId}/quote`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ lines }),
    });

    const data = await res.json();
    if (!res.ok || !data?.ok) {
      console.error('SAVE_QUOTE_FAILED', data);
      throw new Error(data?.message || 'Devis non enregistré');
    }
  }

  function extractIncludedServiceLabels(priceToShow) {
    const items = Array.isArray(priceToShow?.items) ? priceToShow.items : [];

    const norm = (s) =>
      String(s || '')
        .replace(/\s+/g, ' ')
        .trim();

    return items
      .map((it) => {
        const label = norm(it.label || it.desc || it.description || it.name);
        const role = norm(it.role || it.Role || it.kind || it.type);
        return { label, role };
      })
      .filter((x) => String(x.role || '').toLowerCase() === 'supplement' && x.label)
      .filter((x, i, arr) => arr.findIndex((y) => y.label === x.label) === i)
      .map((x) => x.label); // ✅ on garde uniquement label (string)
  }

  async function goToPayment() {
    try {
      //console.log('%csrc\\b2bTripSelectionindex.jsx:843 state', 'color: #007acc;', state);
      let bookingId = state.bookingId;

      const token = localStorage.getItem('token_b2b');
      const isB2B = !!token; // si tu as isTokenExpired, fais: !!token && !isTokenExpired(token)

      // ✅ base URL API selon B2B/B2C
      // si http a baseURL '/api', alors on met juste:
      const bookingsBase = isB2B ? '/bookings' : '/public/bookings';

      const headers = {
        'Content-Type': 'application/json',
        ...(isB2B ? { Authorization: `Bearer ${token}` } : {}),
      };

      const search = state.search || {};
      const productState = state.product || {};
      const formData = state.formData || {};

      const expectedChildren = Number(search.children); // >= 2 ans
      const expectedInfants = Number(search.infants); // < 2 ans

      console.log(
        '%csrc\b2bTripSelectionindex.jsx:1094 expectedChildren',
        'color: #007acc;',
        expectedChildren,
      );

      const allDOB = Array.isArray(formData.childrenDOB) ? formData.childrenDOB : [];

      // ✅ si on a des enfants, il faut la même longueur
      // if (childrenCount > 0 && childrenDOB.length !== childrenCount) {
      //   alert(`Veuillez renseigner la date de naissance des ${childrenCount} enfant(s).`);
      //   return;
      // }

      const isInfantDOB = (c) => {
        if (c?.isInfant === true) return true;
        const age = Number(c?.ageOnDeparture ?? c?.ageOnEnd);
        return Number.isFinite(age) && age < 2;
      };

      const infantsDOB = allDOB.filter(isInfantDOB);
      const childrenDOB = allDOB.filter((c) => !isInfantDOB(c));

      // ✅ validations réelles
      if (expectedChildren > 0 && childrenDOB.length !== expectedChildren) {
        alert(`Veuillez renseigner la date de naissance des ${expectedChildren} enfant(s).`);
        return;
      }
      if (expectedInfants > 0 && infantsDOB.length !== expectedInfants) {
        alert(`Veuillez renseigner la date de naissance des ${expectedInfants} bébé(s).`);
        return;
      }

      const pgiRoomCode = selectedRoom?.pgiRoomCode || null;
      // console.log(
      //   '%csrc\\b2bTripSelectionindex.jsx:861 pgiRoomCode',
      //   'color: #007acc;',
      //   pgiRoomCode,
      // );

      const pgiRoomId = pgiRoomCode ? pgiRoomCode.match(/^R(\d+)\|/)?.[1] || null : null;

      if (!bookingId) {
        const nightsFromState = state.nights || 0;
        const nightsFromSearch = Number(search.nights || 0);

        const durationNights =
          nightsFromState ||
          nightsFromSearch ||
          Math.max(
            1,
            Math.round((new Date(search.endDate) - new Date(search.startDate)) / 86400000),
          );

        const durationDays = durationNights + 1;

        const payload = {
          productId: state.product?.code || state.productCode || state.productId,
          productName:
            productState?.hotelName || productState?.name || productState?.title || 'Séjour',
          productImage:
            productState.imageUrl || productState.image || productState.picture || null,

          productCode: search.productCode,
          vendorCode: productState.vendorCode || null,
          destination: {
            city: productState.city || '',
            country: productState.country || '',
          },

          from: search.origin,
          fromLabel: search.departCity,
          stayStartDate: search.startDate,
          stayEndDate: search.endDate,
          durationDays,
          durationNights,
          adults: Number(search.adults || 0),
          children: expectedChildren,
          infants: expectedInfants,
          childrenDOB,
          infantsDOB,
          currency: priceToShow?.currency || 'EUR',
          pgiRoomCode,
          includedServices: extractIncludedServiceLabels(priceToShow),
        };

        // ✅ B2B -> /api/bookings ; B2C -> /api/public/bookings (via http baseURL)
        const res = await http.post(bookingsBase, payload, { headers });
        bookingId = res.data?._id || res.data?.booking?._id || res.data?.id || null;
        const guestToken = res.data?.guestToken;
        if (!token && guestToken) {
          localStorage.setItem('booking_guest_token', guestToken);
        }

        //console.log('CREATE_BOOKING payload children=', payload.children);
        //console.log('CREATE_BOOKING payload childrenDOB.length=', payload.childrenDOB?.length);
        //console.log('CREATE_BOOKING payload childrenDOB=', payload.childrenDOB);
      }

      if (!bookingId) {
        console.error('BOOKING_CREATE_RESPONSE', res.data);
        throw new Error('BOOKING_ID_MISSING');
      }

      // ✅ à partir d’ici, bookingId est sûr
      if (selectedRoom) {
        await saveRoom(bookingId, selectedRoom, selectedMealPlan, roomCategories, nights);
      }
      if (selectedFlight?.planId) {
        await saveFlights(bookingId, { ...selectedFlight, grandTotal });
      }

      if (Array.isArray(selectedPrestations) && selectedPrestations.length > 0) {
        await savePrestations(bookingId, selectedPrestations || []);
      }

      await saveQuote(bookingId, roomCategories, selectedPrestations || []);

      // ✅ redirect selon B2B/B2C
      const paymentPath = isB2B ? `/b2b/paiement/${bookingId}` : `/paiement/${bookingId}`;

      history.push(paymentPath, {
        ...state,
        bookingId,
        selectedRoom,
        selectedFlights: selectedFlight,
        roomCategories,
        selectedMealPlan,
        priceSeed: priceToShow,
        computedTotal: grandTotal,
        selectedPrestations,
      });
    } catch (e) {
      console.error('GO_PAYMENT_FAILED', e);
      alert(e?.response?.data?.message || "Impossible d'enregistrer votre sélection.");
    }
  }

  const saveFlight = async (p) => {
    const transportBreakdown = p?.transportBreakdown || {
      description: p?.label || p?.transportLabel,
      unit: p?.transportUnit,
      qty: p?.transportQty,
      currency: p?.transportBreakdown?.currency || p?.plan?.price?.currency || 'EUR',
    };

    setSelectedFlight({
      planId: p?.planId || null,
      transportTotal: Number(p?.transportTotal || 0),
      productCode: p?.plan?.code || p?.code || p?.plan?.productCode || null,
      transportDelta: 0,
      luggagesTotal: Number(p?.luggagesTotal || 0),
      currency: transportBreakdown.currency,
      luggagesSelection: p?.luggagesSelection || null,
      plan: p?.plan || null,
      transportBreakdown,
      segments: p?.segments || p?.plan?.segments || [],
      grandTotal: p?.grandTotal || null,
    });

    if (skipFirstAutoFlightSelectRef.current) {
      skipFirstAutoFlightSelectRef.current = false;
      return;
    }

    if (p?.plan?.code) {
      await repriceWithFlight(p.plan.code);
    }
  };

  const comboTotal = (combo = []) =>
    Number(
      (combo || [])
        .reduce((sum, item) => {
          const qty = Math.max(1, Number(item?.qty || 1));

          const unit = Number(item?.price?.unit ?? item?.unitTotal ?? item?.unitPrice ?? 0);

          return sum + (Number.isFinite(unit) ? unit * qty : 0);
        }, 0)
        .toFixed(2),
    );

  const roomOptionTotal = (roomOption) => {
    if (!roomOption) return 0;

    const fromCombo = comboTotal(roomOption?.combo);

    if (fromCombo > 0) {
      return fromCombo;
    }

    const candidates = [roomOption?.roomsTotal, roomOption?.total, roomOption?.price?.total];

    for (const value of candidates) {
      const amount = Number(value);

      if (Number.isFinite(amount) && amount > 0) {
        return Number(amount.toFixed(2));
      }
    }

    return 0;
  };

  const defaultRoomOption = useMemo(() => {
    return (
      roomsForUI?.find((r) => r?.ui?.included || r?.ui?.isDefault) ||
      roomsForUI?.[defaultIndex] ||
      roomsForUI?.[0] ||
      null
    );
  }, [roomsForUI, defaultIndex]);

  const defaultRoomsTotal = useMemo(() => {
    return roomOptionTotal(defaultRoomOption);
  }, [defaultRoomOption]);

  const selectedRoomsTotal = useMemo(() => {
    if (!selectedRoom) return defaultRoomsTotal;
    return roomOptionTotal(selectedRoom);
  }, [selectedRoom, defaultRoomsTotal]);

  const roomDelta = useMemo(() => {
    return Number((selectedRoomsTotal - defaultRoomsTotal).toFixed(2));
  }, [selectedRoomsTotal, defaultRoomsTotal]);

  console.log('ROOM DEBUG', {
    defaultRoomsTotal,
    selectedRoomsTotal,
    roomDelta,
    selectedRoomPriceTotal: selectedRoom?.price?.total,
    selectedRoomTotal: selectedRoom?.total,
    selectedRoomSupplement: selectedRoom?.supplement,
  });
  return (
    <div className="container py-3 py-md-4" style={{ marginTop: '3rem' }}>
      <PageLoader show={pending > 0} label="Chargement du devis…" />
      <StepNav product={product} formData={{}} search={search} />

      <div className="row g-4">
        <div className="col-lg-8">
          <div className="card mb-3">
            <div className="card-header fw-semibold font-size-laarge text-dark">
              1 - Répartition des voyageurs
            </div>
            <div className="card-body" style={{ padding: '0rem 2rem 2rem 2rem' }}>
              {err && <div className="alert alert-danger small mb-3">{err}</div>}
              <SelectRoom
                rooms={roomsUI}
                adults={search.adults}
                children={search.children}
                infants={search.infants}
                nights={nights}
                basePriceTotal={defaultRoomsTotal}
                defaultIndex={defaultIndex}
                selectedCode={selectedRoom?.code || null}
                onSelect={(payload) => {
                  const infants = Number(search.infants || 0);
                  const fixedCombo = distributePaxOverCombo(
                    Array.isArray(payload?.combo) ? payload.combo : [],
                    Number(search.adults || 0),
                    Number(search.children || 0),
                    infants,
                  );

                  // on enrichit le combo avec allocation.infants
                  const fixedComboWithInfants = fixedCombo.map((item) => {
                    const alloc = {
                      adults: Number(item.allocation?.adults || 0),
                      children: Number(item.allocation?.children || 0),
                      infants: Number(infants || 0),
                    };

                    const pax = alloc.adults + alloc.children + alloc.infants;
                    const roomRef =
                      (item.value && item.value.split('|')[0]) || item.roomRef || item.code;

                    return {
                      ...item,
                      allocation: alloc,
                      value: `${roomRef}|adt:${alloc.adults},chd:${alloc.children},inf:${alloc.infants},pax:${pax}`,
                    };
                  });

                  // 🔍 On essaie de retrouver le pgiCode correspondant dans `rooms`
                  let pgiRoomCode = null;
                  if (Array.isArray(rooms) && rooms.length && fixedComboWithInfants.length) {
                    const main = fixedComboWithInfants[0]; // on prend la 1ère chambre du combo
                    const norm = (s) =>
                      String(s || '')
                        .replace(/\s+/g, ' ')
                        .trim()
                        .toLowerCase();

                    const match = rooms.find((r) => {
                      // on compare soit la description, soit le code
                      const rDesc = norm(r.description || r.desc);
                      const rCode = norm(r.code);
                      const mDesc = norm(main.description || main.desc);
                      const mCode = norm(main.code);

                      return (rDesc && rDesc === mDesc) || (rCode && rCode === mCode);
                    });

                    if (match && match.pgiCode) {
                      pgiRoomCode = match.pgiCode;
                    }
                  }

                  const selectedRoomsTotalNow = comboTotal(fixedComboWithInfants);

                  const supplement = Number(
                    Math.max(0, selectedRoomsTotalNow - defaultRoomsTotal).toFixed(2),
                  );

                  const fullPayload = {
                    ...payload,

                    combo: fixedComboWithInfants,
                    pgiRoomCode,

                    roomsTotal: selectedRoomsTotalNow,
                    total: selectedRoomsTotalNow,

                    price: {
                      ...(payload?.price || {}),
                      total: selectedRoomsTotalNow,
                      currency: payload?.price?.currency || 'EUR',
                    },

                    defaultRoomsTotal,
                    supplement,
                  };
                  setSelectedRoom(fullPayload);
                  setSelectedCombo(fixedComboWithInfants);
                  setRoomCategories(buildPerRoomCategories(fixedComboWithInfants, infants));

                  //console.log('selectedRoom', fullPayload);
                }}
              />
            </div>
          </div>

          {selectedCombo.length > 0 && (
            <div className="card mb-3">
              <div className="card-header fw-semibold font-size-laarge text-dark">
                2 - Catégorie d’hébergement
              </div>
              <div className="card-body" style={{ padding: '0rem 2rem 2rem 2rem' }}>
                <RoomCategories
                  combo={selectedCombo}
                  // adults={Number(search.adults || 0)}
                  // children={Number(search.children || 0)}
                  // infants={Number(search.infants || 0)}
                />
              </div>
            </div>
          )}

          {mealPlans.length > 0 && (
            <div className="card mb-3">
              <div className="card-header fw-semibold font-size-laarge text-dark">
                3 - Type de pension
              </div>
              <div className="card-body" style={{ padding: '0rem 2rem 2rem 2rem' }}>
                <SelectMealPlan
                  plans={mealPlans}
                  selectedCode={selectedMealPlan?.code || null}
                  onSelect={(p) => setSelectedMealPlan(p)}
                />
              </div>
            </div>
          )}

          {!noFlight && (
            <div className="card">
              <div className="d-flex justify-content-between card-header fw-semibold font-size-laarge text-dark">
                4 - Transport
              </div>

              <div className="card-body" style={{ padding: '0rem 2rem 2rem 2rem' }}>
                <SelectFlight
                  flights={flightPlans}
                  origin={search.origin}
                  destination={search.destination}
                  startDate={search.startDate}
                  endDate={search.endDate}
                  adults={Number(search?.adults)}
                  children={Number(search?.children || 0)}
                  selectedPlanId={selectedFlight?.planId || null}
                  initialLuggagesSelection={selectedFlight?.luggagesSelection || null}
                  onSelect={saveFlight}
                />
              </div>
            </div>
          )}

          {includedServices.length > 0 && (
            <div className="card mt-3">
              <div className="card-header fw-semibold font-size-laarge text-dark">
                5 - Prestations incluses
              </div>

              <div className="card-body" style={{ padding: '0rem 2rem 2rem 2rem' }}>
                {includedServices.map((s, idx) => (
                  <div
                    key={`${s.label}-${idx}`}
                    className="d-flex align-items-center justify-content-between border rounded p-3 mb-2"
                  >
                    <div>{s.label}</div>

                    <span className="badge bg-success-subtle text-success border border-success-subtle">
                      inclus
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {prestations.length > 0 && (
            <div className="card mt-3">
              <div className="card-header fw-semibold font-size-laarge text-dark">
                6 - Prestations
              </div>

              <div className="card-body" style={{ padding: '0rem 2rem 2rem 2rem' }}>
                <AddOnsSelection
                  groups={prestations}
                  selected={selectedPrestations}
                  paxCount={Number(search.adults || 0) + Number(search.children || 0) || 1}
                  onChange={setSelectedPrestations}
                />
              </div>
            </div>
          )}
        </div>

        <div className="col-lg-4">
          <QuoteSummary
            product={product}
            search={search}
            price={priceToShow}
            extras={{
              room: roomDelta,
              roomDelta,
              defaultRoomsTotal,
              selectedRoomsTotal,
              flights: flightsTotal,
              transportDelta: selectedFlight?.transportDelta || 0,
              luggagesTotal: selectedFlight?.luggagesTotal || 0,
              luggagesSelection: selectedFlight?.luggagesSelection || null,
              grand: grandTotal,
              prestationsTotal,
              prestations: selectedPrestations,

              mealPlan: selectedMealPlan
                ? {
                    desc: selectedMealPlan.desc,
                    // total vient de PGI, parfois en centimes : on laisse QuoteSummary normaliser
                    total: selectedMealPlan.total,
                    currency: selectedMealPlan.currency || 'EUR',
                    included: !!selectedMealPlan.included,
                  }
                : null,
            }}
            selectedFlights={selectedFlight}
            selectedRoom={selectedRoom}
            discount={b2bDiscount}
            onContinue={goToPayment}
            quoteLoading={quoteLoading}
          />
        </div>
      </div>
    </div>
  );
}
